import React, { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useT } from "@/i18n/i18n";
import { Download, Printer, Hexagon } from "lucide-react";

const ORIGIN = {
  verified_polish: { pl: "Zweryfikowane polskie pochodzenie", en: "Verified Polish origin", color: "#2F6B3C", bg: "#E6F0E8" },
  producer_reported: { pl: "Polskie pochodzenie zgłoszone przez producenta", en: "Producer-reported Polish origin", color: "#8A5A12", bg: "#F6EAD0" },
  packed_in_poland: { pl: "Zapakowano w Polsce (pochodzenie niezweryfikowane)", en: "Packed in Poland (origin not verified)", color: "#7A5C00", bg: "#F3EFD6" },
  pending: { pl: "Weryfikacja w toku", en: "Verification pending", color: "#5A5A5A", bg: "#ECECEC" },
};

const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  .honey-label, .honey-label * { visibility: visible !important; }
  .honey-label { position: absolute !important; left: 0; top: 0; margin: 0 !important; box-shadow: none !important; }
  .no-print { display: none !important; }
  .honey-label--compact { width: 60mm !important; }
  .honey-label--standard { width: 90mm !important; }
  .honey-label--a4 { width: 190mm !important; min-height: 200mm !important; }
}
`;

export default function HoneyLabel({ data, batchId }) {
  const { lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);
  const qrRef = useRef(null);
  const [format, setFormat] = useState("standard");
  const [netWeight, setNetWeight] = useState("");
  const traceUrl = `${window.location.origin}/honey/${batchId}`;
  const origin = ORIGIN[data.origin_status] || ORIGIN.pending;

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.download = `QR-${batchId}.png`;
    a.href = canvas.toDataURL("image/png");
    a.click();
  };

  const width = format === "compact" ? "max-w-[280px]" : format === "a4" ? "max-w-[720px]" : "max-w-[380px]";
  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-3 border-b border-black/5 py-1 text-xs">
      <span className="text-mutedtext">{label}</span><span className="font-medium text-charcoal">{value || "—"}</span>
    </div>
  );

  return (
    <div className="mt-10">
      <style>{PRINT_CSS}</style>
      <div className="no-print mb-4 flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-mutedtext">{L("Format etykiety", "Label format")}</span>
        {[["compact", L("Kompaktowa", "Compact")], ["standard", L("Standardowa", "Standard")], ["a4", "A4"]].map(([k, t]) => (
          <button key={k} onClick={() => setFormat(k)} data-testid={`label-format-${k}`}
            className={`rounded-md border px-3 py-1 text-xs font-semibold transition-colors ${format === k ? "border-forest bg-forest text-white" : "border-subtle text-charcoal hover:border-forest"}`}>{t}</button>
        ))}
        <label className="flex items-center gap-2 text-xs">
          <span className="text-mutedtext">{L("Waga netto", "Net weight")}</span>
          <input value={netWeight} onChange={(e) => setNetWeight(e.target.value)} placeholder="500 g" data-testid="label-net-weight"
            className="w-24 rounded-md border border-input px-2 py-1 text-xs focus:border-forest focus:outline-none" />
        </label>
        <div className="ml-auto flex gap-2">
          <button onClick={downloadQR} data-testid="download-qr-btn" className="inline-flex items-center gap-1.5 rounded-md border border-forest px-3 py-1.5 text-xs font-semibold text-forest hover:bg-forest/5">
            <Download className="h-3.5 w-3.5" /> {L("Pobierz kod QR", "Download QR")}
          </button>
          <button onClick={() => window.print()} data-testid="print-label-btn" className="inline-flex items-center gap-1.5 rounded-md bg-forest px-3 py-1.5 text-xs font-semibold text-white hover:bg-forestdark">
            <Printer className="h-3.5 w-3.5" /> {L("Drukuj etykietę", "Print label")}
          </button>
        </div>
      </div>

      <div className={`honey-label honey-label--${format} ${width} rounded-lg border-2 border-forest/30 bg-white p-4`} data-testid="honey-label">
        <div className="flex items-center gap-2">
          <Hexagon className="h-6 w-6 shrink-0" style={{ color: "#C98A1D" }} aria-hidden="true" />
          <div className="min-w-0">
            <div className="font-heading text-base font-bold leading-tight text-charcoal">Miód Żywiącej Polski</div>
            <div className="text-[11px] text-mutedtext">{data.honey_type}</div>
          </div>
          {data.is_demo && <span className="ml-auto rounded bg-secondary px-1.5 py-0.5 text-[9px] font-bold uppercase text-mutedtext">Demo</span>}
        </div>

        <div className="mt-3 flex gap-3">
          <div className="flex-1">
            <Row label={L("Nr partii", "Batch no.")} value={data.batch_id} />
            <Row label={L("Rok zbioru", "Harvest year")} value={data.harvest_year} />
            <Row label={L("Region", "Region")} value={data.region} />
            <Row label={L("Waga netto", "Net weight")} value={netWeight} />
            {data.producer_publishable && <Row label={L("Producent", "Producer")} value={data.producer} />}
          </div>
          <div ref={qrRef} className="flex shrink-0 flex-col items-center">
            <QRCodeCanvas value={traceUrl} size={format === "compact" ? 72 : 96} includeMargin level="M" data-testid="honey-qr" />
            <span className="mt-1 max-w-[96px] break-all text-center text-[7px] leading-tight text-mutedtext">{data.batch_id}</span>
          </div>
        </div>

        <div className="mt-2 rounded px-2 py-1 text-center text-[10px] font-semibold" style={{ color: origin.color, background: origin.bg }} data-testid="label-origin-status">
          {L(origin.pl, origin.en)}
        </div>

        <p className="mt-2 text-[9px] leading-snug text-mutedtext">
          {L("Zeskanuj kod, aby sprawdzić pochodzenie, historię partii i informacje przekazane przez producenta.",
             "Scan to view the batch origin, traceability history, and information reported by the producer.")}
        </p>
        <p className="mt-1 text-[9px] font-semibold leading-snug" style={{ color: "#8A5A12" }}>
          Miodu nie należy podawać dzieciom poniżej 12. miesiąca życia. · Do not give honey to children under 12 months of age.
        </p>
      </div>
    </div>
  );
}
